import {
  Button,
  Dropdown,
  FormLayout,
  MultiSelectDropdown,
  Spinner,
} from 'components/common'
import DataTableImportButton from 'components/common/dataTable/dataTableImportButton'
import { ImportColumn } from 'components/common/importModal'
import { usePageAccess } from 'hooks'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { CheckboxOptions, InputOptions } from 'types/input'
import { fetchAggregators } from 'utils/api/aggregatorApi'
import { fetchStores } from 'utils/api/storeApi'
import {
  importInventoryBarcodesApi,
  importMasterBarcodesApi,
  importPricingBarcodesApi,
  resetAllInventoryApi,
  resetAllPricingApi,
} from 'utils/api/timestampResetApi'

type ResetModule = 'master' | 'inventory' | 'pricing'

// The API answers with two different error shapes — `{ message }` for unknown
// store ids and `{ errors: { msg: [...] } }` for validator failures — and there
// is no global axios interceptor, so read both here.
const getApiErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data
  const msg = data?.message ?? data?.errors?.msg

  if (typeof msg === 'string') return msg
  if (Array.isArray(msg))
    return msg.map((e: any) => `${e.param}: ${e.msg}`).join(', ')

  return fallback
}

const TimestampResetPage = () => {
  const { isEditor } = usePageAccess()

  const [selectedModule, setSelectedModule] = useState<ResetModule>('master')
  const [allBarcodes, setAllBarcodes] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [storeOptions, setStoreOptions] = useState<CheckboxOptions[]>([])
  const [aggregatorOptions, setAggregatorOptions] = useState<InputOptions[]>([])
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false)
  const [timestampImportColumns, setTimestampImportColumns] = useState<
    ImportColumn[]
  >([])
  // Toggled to clear the store checkboxes — CheckboxGroup re-initializes
  // whenever its `reset` prop changes.
  const [storeResetKey, setStoreResetKey] = useState<boolean>(false)

  const { setValue, watch, reset } = useForm()

  useEffect(() => {
    getImportTemplate('timestampReset')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setTimestampImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadTimestampSample = async () => {
    const response = await downloadImportSample('timestampReset')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'timestamp_reset_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const selectedStores: string[] = watch('stores') ?? []
  const selectedAggregator = watch('aggregator')

  // ─── Fetch dropdowns ───────────────────────────────────────────────────────

  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true)
      try {
        const [{ data: storesData }, { data: aggregatorsData }] =
          await Promise.all([
            fetchStores({ limit: 600, page: 1, isThirdPartyStore: false }),
            fetchAggregators({ limit: 600, page: 1 }),
          ])

        if (storesData?.docs?.length > 0) {
          setStoreOptions(
            storesData.docs
              .map((s: { storeName: string; _id: string }) => ({
                label: s.storeName,
                value: s._id,
                disabled: false,
              }))
              .sort((a: any, b: any) => a.value.localeCompare(b.value)),
          )
        }

        if (aggregatorsData?.docs?.length > 0) {
          setAggregatorOptions(
            aggregatorsData.docs
              .map((a: { aggregatorName: string; _id: string }) => ({
                label: a.aggregatorName,
                value: a._id,
              }))
              .sort((a: any, b: any) => a.value.localeCompare(b.value)),
          )
        }
      } catch (error) {
        toast.error('Failed to load stores or aggregators')
      } finally {
        setLoadingOptions(false)
      }
    }

    fetchOptions()
  }, [])

  // ─── Reset dependent fields on module change ───────────────────────────────

  const handleModuleChange = (module: ResetModule) => {
    setSelectedModule(module)
    setAllBarcodes(false)
    reset({ stores: [], aggregator: null })
    setStoreResetKey((key) => !key)
  }

  // ─── Validation ───────────────────────────────────────────────────────────

  const isFormValid = () => {
    if (selectedModule === 'master') return true
    if (selectedModule === 'inventory') return selectedStores.length > 0
    if (selectedModule === 'pricing')
      return selectedStores.length > 0 && !!selectedAggregator
    return false
  }

  // ─── All barcodes submit ───────────────────────────────────────────────────

  const handleResetAll = async () => {
    if (!isFormValid()) {
      toast.error('Please fill all required fields')
      return
    }

    setIsSubmitting(true)
    const loadingToast = toast.loading('Submitting reset request...', {
      duration: Infinity,
    })

    try {
      if (selectedModule === 'inventory') {
        await resetAllInventoryApi(selectedStores)
      } else if (selectedModule === 'pricing') {
        await resetAllPricingApi(selectedStores, selectedAggregator)
      }

      toast.success('Timestamp reset request submitted successfully', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit reset request'), {
        id: loadingToast,
        duration: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ─── Resolve upload API based on module ───────────────────────────────────

  const getUploadApi = (): ((data: any) => Promise<any>) => {
    if (selectedModule === 'inventory' && selectedStores.length > 0)
      return importInventoryBarcodesApi(selectedStores)
    if (
      selectedModule === 'pricing' &&
      selectedStores.length > 0 &&
      selectedAggregator
    )
      return importPricingBarcodesApi(selectedStores, selectedAggregator)
    return importMasterBarcodesApi
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Timestamp Reset</title>
      </Helmet>

      <div className="mb-2 flex flex-wrap items-center justify-between md:mb-0 md:flex-nowrap">
        <h3 className="title">Timestamp Reset</h3>
      </div>

      <FormLayout className="mt-4 rounded-lg border border-gray-100 bg-white shadow-sm">
        {loadingOptions ? (
          <Spinner />
        ) : (
          <div className="max-w-2xl">
            <h4 className="text-xl font-semibold text-gray-800">
              Reset updatedAt Timestamp
            </h4>
            <p className="mb-6 mt-1 text-sm text-gray-500">
              Select a module and provide the required details to reset
              timestamps.
            </p>

            {/* Module Selection */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Module
              </label>
              <div className="flex gap-6">
                {(['master', 'inventory', 'pricing'] as ResetModule[]).map(
                  (module) => (
                    <label
                      key={module}
                      className="flex cursor-pointer items-center gap-2 text-sm font-medium capitalize text-gray-700"
                    >
                      <input
                        type="radio"
                        name="module"
                        value={module}
                        checked={selectedModule === module}
                        onChange={() => handleModuleChange(module)}
                        className="h-4 w-4 accent-sidebar-primary"
                      />
                      {module.charAt(0).toUpperCase() + module.slice(1)}
                    </label>
                  ),
                )}
              </div>
            </div>

            {/* Stores Dropdown — hidden for master. Deliberately the plain
                MultiSelectDropdown fed by the fetch above rather than the
                shared MultiSelectStoreDropdown, which fetches only limit: 500
                and filters on isActive — both would silently shrink the list
                this page has always shown. */}
            {selectedModule !== 'master' && (
              <div className="mb-4 w-full max-w-xs">
                <MultiSelectDropdown
                  required
                  label="Stores"
                  name="stores"
                  setValue={setValue}
                  includeSelectAll={storeOptions.length > 0}
                  options={
                    storeOptions.length > 0
                      ? storeOptions
                      : [
                          {
                            label: 'No Stores Found',
                            value: 'NO_STORES',
                            disabled: true,
                          },
                        ]
                  }
                  reset={storeResetKey}
                />
              </div>
            )}

            {/* Aggregator Dropdown — only for pricing */}
            {selectedModule === 'pricing' && (
              <div className="mb-4 w-full max-w-xs">
                <Dropdown
                  label="Aggregator"
                  options={aggregatorOptions}
                  name="aggregator"
                  setValue={setValue}
                  defaultValue={false}
                />
              </div>
            )}

            {/* All Barcodes Checkbox — hidden for master */}
            {selectedModule !== 'master' && (
              <div className="mb-6">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={allBarcodes}
                    onChange={(e) => setAllBarcodes(e.target.checked)}
                    className="h-4 w-4 accent-sidebar-primary"
                  />
                  All Barcodes
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  If checked, all barcodes for the selected stores will be
                  reset.
                </p>
                {allBarcodes && selectedStores.length > 1 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Every barcode of the {selectedStores.length} selected stores
                    will be queued for reset. This can take a while.
                  </p>
                )}
              </div>
            )}

            {/* File Upload — always for master, conditional for others */}
            {isEditor && (selectedModule === 'master' || !allBarcodes) && (
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Upload Barcodes CSV
                </label>
                <DataTableImportButton
                  uploadApiFunction={getUploadApi()}
                  disabled={!isFormValid() || isSubmitting}
                  label="Import Barcodes CSV"
                  importColumns={timestampImportColumns}
                  importModuleName="timestamp reset"
                  onDownloadSample={handleDownloadTimestampSample}
                />
              </div>
            )}

            {/* All Barcodes Submit — only for inventory/pricing when checkbox checked */}
            {isEditor && selectedModule !== 'master' && allBarcodes && (
              <div>
                <Button
                  buttonText={
                    isSubmitting ? 'Submitting...' : 'Reset All Timestamps'
                  }
                  type="button"
                  onClick={handleResetAll}
                  disabled={!isFormValid() || isSubmitting}
                  loading={isSubmitting}
                />
              </div>
            )}
          </div>
        )}
      </FormLayout>
    </>
  )
}

export default TimestampResetPage
